import { registerOverlay } from 'klinecharts'
import { type OverlayFigure } from './overlayUtils'
import { CHART_COLORS, CHART_FONT_FAMILY } from './chartColors'
import { inkOn } from './tpoSettings'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam, registerOnce } from './registrationUtils'

/**
 * Programmatic "reference price" line — a full-width dashed horizontal line at
 * an externally-supplied price (e.g. TODAY's live price on an anchored /
 * historical chart), with a matching y-axis price pill. Read-only chart
 * chrome in the spirit of `premarketDailyBar.ts` / `patternOverlay.ts`:
 * locked, id-tracked by ChartManager (`setReferencePriceLine`), never
 * user-drawn and never persisted to `SavedDrawing[]`.
 *
 * Figure construction is pure and unit-tested; `registerReferencePriceLineOverlay`
 * wires the builders into a klinecharts overlay template. The overlay is
 * created with a single `{ value }` point; color + optional tag text ride in
 * `extendData` (canvas overlays can't read CSS vars, so the color is concrete).
 */

export const REFERENCE_PRICE_LINE_NAME = 'referencePriceLine'

export interface ReferencePriceFigureInput {
  /** Pixel y of the reference price (already mapped from value). */
  y: number
  /** Main-pane width in pixels — the line spans the full pane. */
  width: number
  /** The reference price itself (axis pill text). */
  value: number
  /** Decimal places for the axis pill (`precision.price`). */
  pricePrecision: number
  /** Concrete line / pill color. */
  color: string
  /** Optional tag rendered just above the line's left end (e.g. "Today"). */
  label?: string
  /**
   * Optional pixel x where the line STARTS (a rightward ray from an event
   * date, e.g. a dilution level anchored at its filing day) instead of the
   * full-width default. Values below 0 (anchor before the loaded bars) clamp
   * to the left edge, so the line degrades to full-width; an anchor past the
   * right edge means the level is not yet in effect in this window and the
   * whole line is suppressed.
   */
  startX?: number
  /**
   * Optional pixel x where the line ENDS (a level invalidated by price
   * action, e.g. an absorbed offering level). Values past the right edge
   * clamp to the pane width (still-active reads full-width); an end at or
   * left of the pane means the level died before this window and the whole
   * line is suppressed. Omitted = runs to the right edge.
   */
  endX?: number
}

/**
 * Pure builder for the main-pane figures: the full-width dashed line plus an
 * optional left-anchored text tag. Bails to `[]` on a non-finite y
 * (klinecharts would render garbage otherwise).
 */
export function buildReferencePriceFigures(input: ReferencePriceFigureInput): OverlayFigure[] {
  const { y, width, color, label } = input
  if (!Number.isFinite(y)) return []
  const rawStart = input.startX ?? 0
  // Anchored past the right edge: the level starts after this window ends.
  if (rawStart > width) return []
  const startX = Math.max(0, rawStart)
  const rawEnd = input.endX ?? width
  // Ended at or before the left edge: the level died before this window.
  if (rawEnd <= 0) return []
  const endX = Math.min(width, rawEnd)
  // A collapsed or inverted span has nothing to stroke.
  if (endX <= startX) return []

  const figs: OverlayFigure[] = [
    {
      type: 'line',
      ignoreEvent: true,
      attrs: {
        coordinates: [
          { x: startX, y },
          { x: endX, y },
        ],
      },
      styles: { style: 'dashed', dashedValue: [4, 4], size: 1, color },
    },
  ]

  if (label) {
    // Deliberately quiet: small muted text, no background — the line is a
    // reference, not a call-to-action, so the tag must not out-shout the
    // native chart chrome. Rides the line's start so an anchored ray carries
    // its tag at the event date.
    figs.push({
      type: 'text',
      ignoreEvent: true,
      attrs: { x: startX + 8, y, text: label, align: 'left', baseline: 'bottom' },
      styles: { style: 'none', color, size: 10, family: CHART_FONT_FAMILY },
    })
  }

  return figs
}

/**
 * Pure builder for the y-axis figure: a price pill matching the native
 * `candle.priceMark.last` label — LEFT-aligned at the axis pane's left edge
 * (x=0), where klinecharts anchors its own tick + last-price labels, with the
 * same font metrics as the theme's `last.text`. (The premarket pill's
 * right-flush anchoring reads as misaligned next to native labels — don't
 * copy it here.) Background = line color, text to the pane's price precision.
 */
export function buildReferencePriceAxisFigures(
  input: ReferencePriceFigureInput & { axisWidth: number },
): OverlayFigure[] {
  const { y, value, pricePrecision, color } = input
  if (!Number.isFinite(y)) return []
  return [
    {
      type: 'text',
      ignoreEvent: true,
      attrs: {
        x: 0,
        y,
        text: value.toFixed(pricePrecision),
        align: 'left',
        baseline: 'middle',
      },
      styles: {
        style: 'fill',
        // Derived from the pill background so a light reference-line colour
        // stays legible (hardcoded white was illegible on light fills).
        color: inkOn(color),
        size: 11,
        // Explicit family: 'inherit' is invalid on a canvas and klinecharts
        // would mis-measure the label box (see the theme's last.text note).
        family: CHART_FONT_FAMILY,
        weight: 'normal',
        backgroundColor: color,
        paddingLeft: 4,
        paddingRight: 4,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 2,
      },
    },
  ]
}

/** Register the `referencePriceLine` overlay template with klinecharts (once). */
export const registerReferencePriceLineOverlay = registerOnce(() => {
  registerOverlay({
    name: REFERENCE_PRICE_LINE_NAME,
    totalStep: 2,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      overlay,
      bounding,
      precision,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay: { points: Array<{ value?: number }>; extendData?: unknown }
      bounding: { width: number }
      precision: { price: number }
    }) => {
      const c = coordinates[0]
      const value = overlay.points[0]?.value
      if (!c || value == null) return []
      const ext = (overlay.extendData ?? {}) as { color?: string; label?: string }
      return buildReferencePriceFigures({
        y: c.y,
        width: bounding.width,
        value,
        pricePrecision: precision.price,
        color: ext.color ?? CHART_COLORS.textMuted,
        label: ext.label,
      })
    },
    createYAxisFigures: ({
      coordinates,
      overlay,
      bounding,
      precision,
    }: {
      coordinates: Array<{ x: number; y: number }>
      overlay: { points: Array<{ value?: number }>; extendData?: unknown }
      bounding: { width: number }
      precision: { price: number }
    }) => {
      const c = coordinates[0]
      const value = overlay.points[0]?.value
      if (!c || value == null) return []
      const ext = (overlay.extendData ?? {}) as { color?: string; label?: string }
      return buildReferencePriceAxisFigures({
        y: c.y,
        width: 0,
        axisWidth: bounding.width,
        value,
        pricePrecision: precision.price,
        color: ext.color ?? CHART_COLORS.textMuted,
      })
    },
  } as OverlayTemplateParam)
})
